import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { User, MapPin, Phone, Mail, Calendar, AlertTriangle, CheckCircle, Edit, Building2, Star, TrendingUp, DollarSign, Store, Save, X } from 'lucide-react'
import { api } from '../../lib/api'
import AdminNavHeader from '../../components/AdminNavHeader'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface StoreOwner {
  id: string
  name: string
  email: string
  phone: string
  is_active: boolean
  created_at: string
  store: {
    id: string
    name: string
    status: string
    is_active: boolean
    address: string
    phone: string
    email: string
  } | null
}

const StoreOwnerDetails = () => {
  const { ownerId } = useParams<{ ownerId: string }>()
  const navigate = useNavigate()
  const [owner, setOwner] = useState<StoreOwner | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: ''
  })

  useEffect(() => {
    if (ownerId) {
      // Check if we're in edit mode
      const isEditMode = window.location.pathname.endsWith('/edit')
      setIsEditing(isEditMode)
      loadOwnerDetails()
    }
  }, [ownerId])

  useEffect(() => {
    if (owner && !isEditing) {
      setEditForm({
        name: owner.name,
        email: owner.email,
        phone: owner.phone
      })
    }
  }, [owner, isEditing])

  const loadOwnerDetails = async () => {
    if (!ownerId) return
    
    const cacheKey = `admin_store_owner_details_${ownerId}`
    
    // Intentar cargar desde cache primero
    const cachedOwner = getCacheStale<StoreOwner>(cacheKey)
    if (cachedOwner) {
      setOwner(cachedOwner)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/admin/store-owners`)
      const foundOwner = response.data.data.find((o: StoreOwner) => o.id === ownerId)
      if (foundOwner) {
        setOwner(foundOwner)
        
        // Guardar en cache (5 minutos de TTL)
        setCache(cacheKey, foundOwner, 5 * 60 * 1000)
      } else {
        navigate('/admin/store-owners')
      }
    } catch (error: any) {
      console.error('Error loading owner details:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOwner) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      navigate('/admin/store-owners')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleSaveEdit = async () => {
    if (!owner) return

    try {
      await api.patch(`/admin/users/${owner.id}`, editForm)
      setOwner({ ...owner, ...editForm })
      setIsEditing(false)
    } catch (error) {
      console.error('Error updating owner:', error)
    }
  }

  const handleCancelEdit = () => {
    setEditForm({
      name: owner?.name || '',
      email: owner?.email || '',
      phone: owner?.phone || ''
    })
    setIsEditing(false)
  }

  const handleStatusChange = async (userId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, { isActive })
      if (owner) {
        setOwner({ ...owner, is_active: isActive })
      }
    } catch (error) {
      console.error('Error updating owner status:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando detalles...</p>
        </div>
      </div>
    )
  }

  if (!owner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Dueño no encontrado</h2>
          <p className="text-gray-600 mb-4">El dueño de negocio solicitado no existe.</p>
          <button
            onClick={() => navigate('/admin/store-owners')}
            className="btn-primary"
          >
            Volver a Dueños
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <AdminNavHeader
        title={isEditing ? 'Editar Dueño' : 'Detalles del Dueño'}
        subtitle={owner ? `${owner.name} - ${owner.email}` : ''}
        backTo="/admin/store-owners"
      />

      {/* Content - Responsive */}
      <div className="px-4 py-6 safe-area-bottom lg:px-8">
        <div className="max-w-4xl mx-auto">
          {isEditing ? (
            /* Edit Mode - Responsive */
            <div className="space-y-6">
              {/* Edit Header */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden">
                <div className="p-6 lg:p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <User className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Editando Dueño</h2>
                      <p className="text-sm lg:text-base text-gray-500">Modifica la información básica</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Edit Form */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 lg:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Name Field */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all text-gray-900"
                      placeholder="Ingresa el nombre completo"
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Correo Electrónico
                    </label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all text-gray-900"
                      placeholder="correo@ejemplo.com"
                    />
                  </div>

                  {/* Phone Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white transition-all text-gray-900"
                      placeholder="809-555-0123"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleCancelEdit}
                  className="flex-1 flex items-center justify-center px-6 py-4 bg-gray-100 rounded-2xl text-base font-semibold text-gray-700 hover:bg-gray-200 transition-colors active:scale-95"
                >
                  <X className="w-5 h-5 mr-2" />
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 flex items-center justify-center px-6 py-4 bg-primary-500 rounded-2xl text-base font-semibold text-white hover:bg-primary-600 transition-colors active:scale-95"
                >
                  <Save className="w-5 h-5 mr-2" />
                  Guardar Cambios
                </button>
              </div>
            </div>
          ) : (
            /* View Mode - Responsive */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Personal Info & Actions */}
              <div className="lg:col-span-2 space-y-6">
                {/* Owner Header Card */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden">
                  <div className="p-6 lg:p-8">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-6">
                      <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <User className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl lg:text-2xl font-bold text-gray-900">{owner.name}</h2>
                        <p className="text-sm lg:text-base text-gray-500">Dueño de Negocio</p>
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium mt-3 ${
                          owner.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {owner.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={handleEdit}
                        className="flex items-center justify-center px-4 py-3 bg-white rounded-xl text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors active:scale-95"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </button>
                      <button
                        onClick={() => {/* TODO: Implement notification functionality */}}
                        className="flex items-center justify-center px-4 py-3 bg-white rounded-xl text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors active:scale-95"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Mensaje
                      </button>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 lg:p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mr-4">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Información Personal</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ml-14">
                    <div className="flex items-center text-sm">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <Mail className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                        <p className="text-gray-700 truncate">{owner.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center text-sm">
                      <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <Phone className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Teléfono</p>
                        <p className="text-gray-700">{owner.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center text-sm">
                      <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <Calendar className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Registrado</p>
                        <p className="text-gray-700 text-xs">
                          {new Date(owner.created_at).toLocaleDateString('es-DO', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Store Information */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 lg:p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center mr-4">
                      <Building2 className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Información del Negocio</h3>
                  </div>
                  {owner.store ? (
                    <div className="ml-14">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center text-sm">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <Building2 className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Nombre</p>
                            <p className="text-gray-900 font-medium">{owner.store.name}</p>
                          </div>
                        </div>
                        <div className="flex items-start text-sm">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            <MapPin className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Dirección</p>
                            <p className="text-gray-700 leading-tight">{owner.store.address}</p>
                          </div>
                        </div>
                        <div className="flex items-center text-sm">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <Mail className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                            <p className="text-gray-700 truncate">{owner.store.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center text-sm">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <Phone className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Teléfono</p>
                            <p className="text-gray-700">{owner.store.phone}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-center space-x-3 pt-4 border-t border-gray-100">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                          owner.store.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : owner.store.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {owner.store.status === 'approved' ? 'Aprobado' :
                           owner.store.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                          owner.store.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {owner.store.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-50/80 rounded-xl p-4 border border-amber-200/50 ml-14">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800">Sin Negocio Registrado</p>
                          <p className="text-xs text-amber-700">Este usuario aún no ha registrado un negocio</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Statistics & Actions */}
              <div className="space-y-6">
                {/* Statistics Section */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 lg:p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center mr-4">
                      <TrendingUp className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Estadísticas</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50/50 rounded-xl p-4 text-center border border-gray-100/50">
                      <Store className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">{owner.store ? 1 : 0}</p>
                      <p className="text-xs text-gray-500">Negocios</p>
                    </div>
                    <div className="bg-gray-50/50 rounded-xl p-4 text-center border border-gray-100/50">
                      <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">-</p>
                      <p className="text-xs text-gray-500">Ventas</p>
                    </div>
                    <div className="bg-gray-50/50 rounded-xl p-4 text-center border border-gray-100/50">
                      <CheckCircle className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">-</p>
                      <p className="text-xs text-gray-500">Pedidos</p>
                    </div>
                    <div className="bg-gray-50/50 rounded-xl p-4 text-center border border-gray-100/50">
                      <Star className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">-</p>
                      <p className="text-xs text-gray-500">Rating</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 text-center">
                    Las métricas detalladas estarán disponibles próximamente
                  </p>
                </div>

                {/* Status Change Actions */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 lg:p-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones</h3>
                  {owner.is_active ? (
                    <button
                      onClick={() => handleStatusChange(owner.id, false)}
                      className="w-full flex items-center justify-center px-4 py-3 bg-red-50 rounded-xl text-sm font-medium text-red-700 border border-red-200 hover:bg-red-100 transition-colors active:scale-95"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Suspender Dueño
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStatusChange(owner.id, true)}
                      className="w-full flex items-center justify-center px-4 py-3 bg-green-50 rounded-xl text-sm font-medium text-green-700 border border-green-200 hover:bg-green-100 transition-colors active:scale-95"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Activar Dueño
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default StoreOwnerDetails
